import { useState, useCallback } from "react"
import {Download, FileText, FileSpreadsheet, Printer, ChevronDown, Check} from 'lucide-react'

export interface ExportRow {
  label: string
  value: string
}

interface ExportButtonsProps {
  title: string
  subtitle?: string
  rows: ExportRow[]
  accentColor?: "emerald" | "sky"
  note?: string
}

export default function ExportButtons({ title, subtitle, rows, accentColor = "emerald", note }: ExportButtonsProps) {
  const [open, setOpen] = useState(false)
  const [exported, setExported] = useState<string | null>(null)

  const showSuccess = useCallback((type: string) => {
    setExported(type)
    setTimeout(() => setExported(null), 2000)
  }, [])

  const handlePrint = useCallback(() => {
    setOpen(false)
    const printWin = window.open("", "_blank", "width=700,height=600")
    if (!printWin) return

    const accent = accentColor === "sky" ? "#0ea5e9" : "#10b981"
    const html = `<!DOCTYPE html>
<html><head><title>${title}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; padding: 48px; color: #1e293b; background: #fff; }
  .header { border-bottom: 3px solid ${accent}; padding-bottom: 16px; margin-bottom: 32px; }
  .header h1 { font-size: 22px; color: ${accent}; margin-bottom: 4px; }
  .header p { font-size: 13px; color: #64748b; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 12px 16px; text-align: left; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
  th { background: #f8fafc; color: #475569; font-weight: 600; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
  td:last-child { text-align: right; font-weight: 600; color: #0f172a; }
  .note { margin-top: 24px; padding: 12px 16px; background: #f0fdf4; border-left: 3px solid ${accent}; font-size: 12px; color: #475569; border-radius: 0 6px 6px 0; }
  .footer { margin-top: 32px; text-align: center; font-size: 11px; color: #94a3b8; }
  @media print { body { padding: 24px; } }
</style>
</head><body>
<div class="header">
  <h1>${title}</h1>
  <p>${subtitle || "Generated on " + new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
</div>
<table>
  <thead><tr><th>Metric</th><th>Value</th></tr></thead>
  <tbody>${rows.map(r => `<tr><td>${r.label}</td><td>${r.value}</td></tr>`).join("")}</tbody>
</table>
${note ? `<div class="note">${note}</div>` : ""}
<div class="footer">FatIntakeCalculator.com &mdash; ${new Date().toLocaleDateString()}</div>
</body></html>`

    printWin.document.write(html)
    printWin.document.close()
    printWin.focus()
    setTimeout(() => { printWin.print(); printWin.close() }, 400)
    showSuccess("print")
  }, [title, subtitle, rows, note, accentColor, showSuccess])

  const handlePDF = useCallback(async () => {
    setOpen(false)
    const { default: jsPDF } = await import("jspdf")
    await import("jspdf-autotable")

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
    const accent = accentColor === "sky" ? [14, 165, 233] : [16, 185, 129]
    const pageW = doc.internal.pageSize.getWidth()

    // Header bar
    doc.setFillColor(accent[0], accent[1], accent[2])
    doc.rect(0, 0, pageW, 28, "F")
    doc.setFont("helvetica", "bold")
    doc.setFontSize(16)
    doc.setTextColor(255, 255, 255)
    doc.text(title, 16, 17)

    // Subtitle
    doc.setFontSize(9)
    doc.setTextColor(200, 200, 200)
    doc.text(
      subtitle || `Generated on ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
      16,
      24
    )

    // Table
    const tableData = rows.map(r => [r.label, r.value])
    ;(doc as any).autoTable({
      startY: 36,
      head: [["Metric", "Value"]],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 6,
        lineColor: [226, 232, 240],
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: [71, 85, 105],
        fontStyle: "bold",
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [30, 41, 59],
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      columnStyles: {
        1: { halign: "right", fontStyle: "bold" },
      },
      margin: { left: 16, right: 16 },
    })

    // Note
    const finalY = (doc as any).lastAutoTable?.finalY || 100
    if (note) {
      doc.setFillColor(240, 253, 244)
      doc.roundedRect(16, finalY + 8, pageW - 32, 16, 2, 2, "F")
      doc.setFontSize(8)
      doc.setTextColor(100, 116, 139)
      doc.text(note, 20, finalY + 18, { maxWidth: pageW - 40 })
    }

    // Footer
    doc.setFontSize(8)
    doc.setTextColor(148, 163, 184)
    doc.text(
      `FatIntakeCalculator.com | ${new Date().toLocaleDateString()}`,
      pageW / 2,
      285,
      { align: "center" }
    )

    doc.save(`${title.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`)
    showSuccess("pdf")
  }, [title, subtitle, rows, note, accentColor, showSuccess])

  const handleExcel = useCallback(async () => {
    setOpen(false)
    const XLSX = await import("xlsx")

    const wsData = [
      [title],
      [subtitle || `Generated on ${new Date().toLocaleDateString()}`],
      [],
      ["Metric", "Value"],
      ...rows.map(r => [r.label, r.value]),
    ]

    if (note) {
      wsData.push([], ["Note:", note])
    }

    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // Column widths
    ws["!cols"] = [{ wch: 35 }, { wch: 25 }]

    // Merge title row
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Results")
    XLSX.writeFile(wb, `${title.replace(/[^a-zA-Z0-9]/g, "_")}.xlsx`)
    showSuccess("excel")
  }, [title, subtitle, rows, note, showSuccess])

  const isEmerald = accentColor === "emerald"

  const actions = [
    {
      id: "pdf",
      label: "Download PDF",
      icon: FileText,
      onClick: handlePDF,
      desc: "Formatted report",
    },
    {
      id: "excel",
      label: "Download Excel",
      icon: FileSpreadsheet,
      onClick: handleExcel,
      desc: "Spreadsheet file",
    },
    {
      id: "print",
      label: "Print Results",
      icon: Printer,
      onClick: handlePrint,
      desc: "Send to printer",
    },
  ]

  return (
    <div className="relative">
      {/* Main trigger button */}
      <button
        onClick={() => setOpen(!open)}
        className={`group flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 border-2 ${
          isEmerald
            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:border-emerald-500/50"
            : "border-sky-400/30 bg-sky-500/10 text-sky-600 hover:bg-sky-500/20 hover:border-sky-400/50"
        } ${exported ? "scale-95" : ""}`}
      >
        {exported ? (
          <>
            <Check className="w-4 h-4" />
            <span>Exported!</span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            <span>Export</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={`absolute right-0 top-full mt-2 z-50 w-56 rounded-xl border shadow-xl overflow-hidden animate-fade-in-up ${
              isEmerald
                ? "bg-slate-800 border-white/10"
                : "bg-white border-gray-200"
            }`}
          >
            {actions.map((action) => {
              const Icon = action.icon
              return (
                <button
                  key={action.id}
                  onClick={action.onClick}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
                    isEmerald
                      ? "hover:bg-white/5 text-gray-300 hover:text-white"
                      : "hover:bg-gray-50 text-slate-700 hover:text-slate-900"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isEmerald ? "bg-white/5" : "bg-gray-100"
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${
                      action.id === "pdf"
                        ? "text-red-400"
                        : action.id === "excel"
                        ? "text-green-400"
                        : isEmerald ? "text-blue-400" : "text-blue-500"
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold leading-tight">{action.label}</p>
                    <p className={`text-xs ${isEmerald ? "text-gray-500" : "text-slate-400"}`}>{action.desc}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
